import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Users, Pencil, Building2, Hash } from "lucide-react";

const API = '/api';

export default function Shooters() {
  const { getAuthHeader } = useAuth();
  const [shooters, setShooters] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShooter, setEditingShooter] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    shooter_id: "",
    email: "",
    phone: "",
    sponsor_id: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const t = Date.now();
      const [shootersRes, sponsorsRes] = await Promise.all([
        axios.get(`${API}/shooters?t=${t}`),
        axios.get(`${API}/sponsors?t=${t}`),
      ]);
      setShooters(shootersRes.data);
      setSponsors(sponsorsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    const payload = {
      name: formData.name.trim(),
      shooter_id: formData.shooter_id || null,
      email: formData.email || null,
      phone: formData.phone || null,
      sponsor_id: formData.sponsor_id && formData.sponsor_id !== "none" ? formData.sponsor_id : null,
    };

    try {
      if (editingShooter) {
        await axios.put(`${API}/shooters/${editingShooter.id}`, payload, {
          headers: getAuthHeader()
        });
        toast.success("Shooter updated");
      } else {
        await axios.post(`${API}/shooters`, payload, {
          headers: getAuthHeader()
        });
        toast.success("Shooter added");
      }
      setDialogOpen(false);
      resetForm();
      setTimeout(() => fetchData(), 100);
    } catch (error) {
      toast.error("Failed to save shooter");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this shooter?")) return;
    try {
      await axios.delete(`${API}/shooters/${id}`, {
        headers: getAuthHeader()
      });
      toast.success("Shooter deleted");
      setTimeout(() => fetchData(), 100);
    } catch (error) {
      toast.error("Failed to delete shooter");
    }
  };

  const openEditDialog = (shooter) => {
    setEditingShooter(shooter);
    setFormData({
      name: shooter.name,
      shooter_id: shooter.shooter_id || "",
      email: shooter.email || "",
      phone: shooter.phone || "",
      sponsor_id: shooter.sponsor_id || "",
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingShooter(null);
    setFormData({ name: "", shooter_id: "", email: "", phone: "", sponsor_id: "" });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const getSponsorName = (sponsorId) => {
    const sponsor = sponsors.find(s => s.id === sponsorId);
    return sponsor?.name || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="shooters-loading">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="shooters-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Shooters
          </h1>
          <p className="text-slate-500 mt-1">Manage registered shooters</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openNewDialog}
              className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
              data-testid="add-shooter-btn"
            >
              <Plus size={20} className="mr-2" />
              Add Shooter
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[450px]" data-testid="shooter-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">
                {editingShooter ? "Edit Shooter" : "Add Shooter"}
              </DialogTitle>
              <DialogDescription>
                {editingShooter ? "Update shooter details." : "Register a new shooter."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="shooter-name">Name *</Label>
                  <Input
                    id="shooter-name"
                    data-testid="shooter-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter shooter's name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="shooter-id">Shooter ID</Label>
                  <Input
                    id="shooter-id"
                    data-testid="shooter-id-input"
                    value={formData.shooter_id}
                    onChange={(e) => setFormData({ ...formData, shooter_id: e.target.value })}
                    placeholder="e.g., 001, A-123"
                  />
                  <p className="text-xs text-slate-500 mt-1">For search/results display</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="shooter-email">Email</Label>
                  <Input
                    id="shooter-email"
                    type="email"
                    data-testid="shooter-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="shooter-phone">Phone</Label>
                  <Input
                    id="shooter-phone"
                    data-testid="shooter-phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="shooter-sponsor">Sponsor</Label>
                <Select
                  value={formData.sponsor_id}
                  onValueChange={(val) => setFormData({ ...formData, sponsor_id: val })}
                >
                  <SelectTrigger data-testid="shooter-sponsor-select">
                    <SelectValue placeholder="Select sponsor (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Sponsor</SelectItem>
                    {sponsors.map((sponsor) => (
                      <SelectItem key={sponsor.id} value={sponsor.id}>
                        {sponsor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                data-testid="shooter-submit-btn"
              >
                {editingShooter ? "Update Shooter" : "Add Shooter"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Shooters List */}
      {shooters.length === 0 ? (
        <Card data-testid="no-shooters">
          <CardContent className="py-12 text-center">
            <Users className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">No shooters registered yet.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Shooter ID</th>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Sponsor</th>
                  <th className="w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shooters.map((shooter) => (
                  <tr key={shooter.id} data-testid={`shooter-row-${shooter.id}`}>
                    <td className="font-mono text-sm">
                      {shooter.shooter_id ? (
                        <span className="flex items-center gap-1">
                          <Hash size={12} className="text-slate-400" />
                          {shooter.shooter_id}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="font-medium">{shooter.name}</td>
                    <td className="text-sm text-slate-500">
                      {shooter.email && <div>{shooter.email}</div>}
                      {shooter.phone && <div>{shooter.phone}</div>}
                      {!shooter.email && !shooter.phone && <span className="text-slate-400">-</span>}
                    </td>
                    <td>
                      {shooter.sponsor_id ? (
                        <span className="inline-flex items-center gap-1 text-sm text-purple-700">
                          <Building2 size={14} />
                          {getSponsorName(shooter.sponsor_id)}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(shooter)}
                          data-testid={`edit-shooter-${shooter.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(shooter.id)}
                          data-testid={`delete-shooter-${shooter.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      {shooters.length > 0 && (
        <div className="text-sm text-slate-500 text-center">
          Total registered shooters: <span className="font-mono font-medium">{shooters.length}</span>
          {sponsors.length > 0 && (
            <span className="ml-4">
              Sponsored: <span className="font-mono font-medium">
                {shooters.filter(s => s.sponsor_id).length}
              </span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
